// The Northern Reach's places, in the same storybook hand as the rest of the
// map but built for the north: turf roofs, tarred timber and falu-red
// boards, snow on every ledge. Five open an app that had no place on the map
// (the guild hall, the world port, Aurora point, Bottle cove, the pigeon
// loft); five are coming soon (the sauna, the ice-fishing hut, the cairn, the
// seed vault, the shipwreck), each with a chalkboard saying what it will be.
// Same parts as the districts: sprites, decals, fields, footprints, lights,
// anchors (for the motion layer), walkways.
import { rng, inkPoly, inkRect, inkEllipse, line, roundRect, mix, withAlpha, softShadow, hatch, blobPath } from "./paint"
import { lantern, cobbles, flagstones } from "./landmarks"

const HAND = '"Bradley Hand", "Segoe Print", "Chalkboard SE", "Comic Neue", cursive'

export function buildReachPlaces(byKey, { seed = 1 } = {}) {
    const out = { sprites: [], decals: [], fields: [], footprints: [], lights: [], anchors: {}, walkways: [] }
    const add = (part) => {
        if (!part) return
        ;["sprites", "decals", "fields", "footprints", "lights", "walkways"].forEach((k) => out[k].push(...(part[k] || [])))
        Object.assign(out.anchors, part.anchors || {})
    }
    const builders = { guildhall, sauna, bottlecove, worldport, icehut, cairn, seedvault, aurora, pigeonloft, shipwreck }
    Object.entries(builders).forEach(([key, build]) => {
        const l = byKey[key]
        if (!l) return
        add(build(l, seed))
        if (l.soon) add(soonBoard(l))
    })
    return out
}

// ---- shared parts ------------------------------------------------------------------

function sign(ctx, pal, x, y, text, color, size = 11) {
    ctx.font = `700 ${size}px ${HAND}`
    const w = ctx.measureText(text).width + 14
    roundRect(ctx, x - w / 2, y - size - 4, w, size + 6, 4)
    ctx.fillStyle = color || pal.cloth; ctx.fill()
    ctx.strokeStyle = pal.ink; ctx.lineWidth = 1.2; ctx.stroke()
    ctx.fillStyle = "#2E3431"; ctx.textAlign = "center"; ctx.textBaseline = "middle"
    ctx.fillText(text, x, y - size / 2 - 1)
    ctx.textBaseline = "alphabetic"
}

// A turf roof: grass over the boards, tufts along the ridge, a few flowers,
// and (on the snowfields) a blanket of snow.
function turfRoof(ctx, pal, poly, { snowy = false, seed = 1 } = {}) {
    inkPoly(ctx, poly, snowy ? pal.snow : pal.turf, pal.ink, { line: 1.7 })
    const r = rng(seed)
    ctx.save()
    ctx.beginPath(); poly.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y))); ctx.closePath(); ctx.clip()
    const xs = poly.map(([x]) => x), ys = poly.map(([, y]) => y)
    const x0 = Math.min(...xs), x1 = Math.max(...xs), y0 = Math.min(...ys), y1 = Math.max(...ys)
    for (let i = 0; i < (x1 - x0) * (y1 - y0) * 0.02; i++) {
        const x = x0 + r() * (x1 - x0), y = y0 + r() * (y1 - y0)
        if (snowy) { ctx.fillStyle = withAlpha(r() < 0.5 ? pal.snowShade : pal.snowLight, 0.8); ctx.beginPath(); ctx.arc(x, y, 1.4, 0, Math.PI * 2); ctx.fill() } else line(ctx, [[x, y], [x + (r() - 0.5) * 3, y - 4 - r() * 3]], r() < 0.5 ? pal.leafDark : pal.leafLight, 1.1)
    }
    if (!snowy) for (let i = 0; i < 8; i++) { ctx.fillStyle = pal.flowers[Math.floor(r() * pal.flowers.length)]; ctx.beginPath(); ctx.arc(x0 + r() * (x1 - x0), y0 + r() * (y1 - y0), 1.8, 0, Math.PI * 2); ctx.fill() }
    ctx.restore()
    // tufts hanging over the eaves
    const [a, b] = [poly[poly.length - 1], poly[poly.length - 2]]
    for (let t = 0.04; t < 1; t += 0.06) {
        const x = a[0] + (b[0] - a[0]) * t, y = a[1] + (b[1] - a[1]) * t
        line(ctx, [[x, y], [x + (r() - 0.5) * 3, y + 3 + r() * 3]], snowy ? pal.snowShade : pal.leafDark, 1.3)
    }
}

// Horizontal logs, their round ends showing at the corners.
function logWall(ctx, pal, x0, x1, y0, y1, color) {
    inkRect(ctx, x0, y1, x1 - x0, y0 - y1, color, pal.ink, { line: 1.6 })
    for (let y = y0 - 8; y > y1; y -= 8) line(ctx, [[x0, y], [x1, y]], mix(color, "#000000", 0.3), 1.1, 0.75)
    for (let y = y0 - 4; y > y1; y -= 8) { inkEllipse(ctx, x0, y, 4, 3.6, pal.woodLight, pal.ink, 0.9); inkEllipse(ctx, x1, y, 4, 3.6, pal.woodLight, pal.ink, 0.9) }
}

function snowOn(ctx, pal, x0, x1, y, depth = 4) {
    ctx.fillStyle = pal.snowLight
    ctx.beginPath(); ctx.moveTo(x0 - 2, y); ctx.quadraticCurveTo((x0 + x1) / 2, y - depth * 2, x1 + 2, y); ctx.quadraticCurveTo((x0 + x1) / 2, y + 1.5, x0 - 2, y); ctx.fill()
    ctx.strokeStyle = withAlpha(pal.snowDeep, 0.7); ctx.lineWidth = 0.8; ctx.stroke()
}

// The chalkboard in front of a place that isn't open yet: "Coming soon" and
// what it will be. The place card offers "Want this?" (see the controller).
function soonBoard(l) {
    const x = l.x - l.r * 0.55, y = l.y + l.r * 0.35
    return {
        sprites: [{
            x, y, box: [-34, -64, 34, 6],
            paint(ctx, pal) {
                const ink = pal.ink
                line(ctx, [[-20, 0], [-12, -52]], pal.woodDark, 3)
                line(ctx, [[20, 0], [12, -52]], pal.woodDark, 3)
                inkRect(ctx, -26, -54, 52, 36, pal.woodDark, ink, { line: 1.5 })
                inkRect(ctx, -22, -50, 44, 28, pal.night ? "#1E2A26" : "#2F3D36", ink, { line: 1 })
                ctx.fillStyle = "#F4F1E6"; ctx.textAlign = "center"; ctx.textBaseline = "middle"
                ctx.font = `700 7px ${HAND}`; ctx.fillText("COMING SOON", 0, -43)
                ctx.font = `700 9px ${HAND}`; ctx.fillStyle = "#F2C14E"; ctx.fillText(String(l.soon).slice(0, 13), 0, -31)
                ctx.textBaseline = "alphabetic"
                line(ctx, [[-16, -25], [16, -25]], "rgba(244,241,230,0.4)", 0.8)
            }
        }],
        footprints: [{ circle: true, x, y: y - 2, r: 12 }]
    }
}

function barrel(x, y, s = 1) {
    return {
        x, y, box: [-14 * s, -30 * s, 14 * s, 4],
        paint(ctx, pal) {
            const ink = pal.ink
            inkPoly(ctx, [[-10 * s, 0], [-12 * s, -13 * s], [-10 * s, -26 * s], [10 * s, -26 * s], [12 * s, -13 * s], [10 * s, 0]], pal.wood, ink, { line: 1.3 })
            ;[-5, -21].forEach((y) => line(ctx, [[-11 * s, y * s], [11 * s, y * s]], pal.metal, 2))
            inkEllipse(ctx, 0, -26 * s, 10 * s, 3 * s, pal.woodDark, ink, 1.1)
        }
    }
}

function crate(x, y, s, label, tilt = 0) {
    return {
        x, y, box: [-s * 1.3, -s * 1.8, s * 1.3, 5],
        paint(ctx, pal) {
            const ink = pal.ink
            ctx.save(); ctx.rotate(tilt)
            inkPoly(ctx, [[s * 0.9, 0], [s * 1.2, -s * 0.25], [s * 1.2, -s * 1.15], [s * 0.9, -s * 0.9]], pal.woodDark, ink, { line: 1.2 })
            inkPoly(ctx, [[-s * 0.9, -s * 0.9], [-s * 0.6, -s * 1.15], [s * 1.2, -s * 1.15], [s * 0.9, -s * 0.9]], pal.woodLight, ink, { line: 1.2 })
            inkRect(ctx, -s * 0.9, -s * 0.9, s * 1.8, s * 0.9, pal.wood, ink, { line: 1.3 })
            line(ctx, [[-s * 0.9, -s * 0.9], [s * 0.9, 0]], pal.woodDark, 1.4)
            if (label) { ctx.fillStyle = withAlpha(pal.ink, 0.7); ctx.font = `700 ${Math.round(s * 0.36)}px ${HAND}`; ctx.textAlign = "center"; ctx.fillText(label, 0, -s * 0.34) }
            ctx.restore()
        }
    }
}

// ---- the birch edge: the guild hall (Recruiter) ---------------------------------------
// A long turf-roofed hall of tarred timber with carved dragon heads on the
// gables. Company banners hang along the front on their poles, and a board of
// open roles stands by the door.

function guildhall(l) {
    const baseY = l.y - 16, w = 230, h = 70
    const banners = [["#3E6FB0", "#F2C14E"], ["#B8433A", "#FAF7EF"], ["#3E8F6A", "#FAF7EF"], ["#6A4FA3", "#F2C14E"], ["#E8894A", "#2E3431"]]
    const bannerXs = [-150, -118, 100, 132]
    return {
        sprites: [{
            x: l.x, y: baseY, box: [-150, -196, 176, 10],
            paint(ctx, pal) {
                const ink = pal.ink, tar = pal.night ? "#3A2E26" : "#6B4A36"
                // the end wall, in depth
                inkPoly(ctx, [[w / 2, 0], [w / 2 + 34, -16], [w / 2 + 34, -h - 16], [w / 2, -h]], mix(tar, "#000000", 0.25), ink)
                // the long wall: vertical boards
                inkRect(ctx, -w / 2, -h, w, h, tar, ink, { line: 1.8 })
                for (let x = -w / 2 + 9; x < w / 2; x += 9) line(ctx, [[x, -h], [x, 0]], mix(tar, "#000000", 0.3), 1, 0.75)
                // carved posts
                ;[-w / 2 + 4, -42, 42, w / 2 - 4].forEach((x) => inkRect(ctx, x - 4, -h, 8, h, pal.woodDark, ink, { line: 1.2 }))
                // the end wall's gable, then the turf roof over the long side
                inkPoly(ctx, [[w / 2, -h], [w / 2 + 34, -h - 16], [w / 2 + 14, -h - 74]], mix(tar, "#000000", 0.35), ink)
                turfRoof(ctx, pal, [[-w / 2 - 6, -h - 70], [w / 2 - 6, -h - 70], [w / 2 + 40, -h - 6], [-w / 2 - 22, -h + 8]], { seed: 42 })
                // barge boards crossing into dragon heads at the gable
                ;[[-w / 2 - 18, -h + 6, -w / 2 + 2, -h - 78, -1], [w / 2 + 12, -h + 6, w / 2 - 12, -h - 78, 1]].forEach(([x0, y0, x1, y1, side]) => {
                    line(ctx, [[x0, y0], [x1, y1]], ink, 6); line(ctx, [[x0, y0], [x1, y1]], pal.woodLight, 3.6)
                    ctx.save(); ctx.translate(x1, y1)
                    ctx.beginPath(); ctx.moveTo(0, 0); ctx.quadraticCurveTo(side * 14, -18, side * 4, -30); ctx.quadraticCurveTo(side * -2, -24, side * 6, -16); ctx.quadraticCurveTo(side * 2, -8, 0, 0)
                    ctx.fillStyle = pal.woodLight; ctx.fill(); ctx.strokeStyle = ink; ctx.lineWidth = 1.4; ctx.stroke()
                    ctx.fillStyle = ink; ctx.beginPath(); ctx.arc(side * 6, -22, 1.4, 0, Math.PI * 2); ctx.fill()
                    ctx.restore()
                })
                // double doors with iron straps
                inkRect(ctx, -26, -54, 52, 54, pal.woodDark, ink, { line: 1.6 })
                line(ctx, [[0, -54], [0, 0]], ink, 1.2)
                ;[-44, -14].forEach((y) => { line(ctx, [[-24, y], [24, y]], pal.metal, 2.4); ;[-16, 16].forEach((x) => inkEllipse(ctx, x, y, 2, 2, pal.metal, ink, 0.8)) })
                inkEllipse(ctx, -6, -28, 3, 3, null, pal.metal, 1.6); inkEllipse(ctx, 6, -28, 3, 3, null, pal.metal, 1.6)
                // shuttered windows glowing
                ;[-84, 84].forEach((x) => {
                    inkRect(ctx, x - 13, -52, 26, 20, pal.night ? pal.windowLit : pal.window, ink, { line: 1.3 })
                    line(ctx, [[x, -52], [x, -32]], pal.woodDark, 1.6)
                    inkRect(ctx, x - 21, -52, 7, 20, pal.falu, ink, { line: 1 }); inkRect(ctx, x + 14, -52, 7, 20, pal.falu, ink, { line: 1 })
                })
                // the sign over the door
                sign(ctx, pal, 0, -62, "GUILD HALL", "#F6E7C4", 12)
                lantern(ctx, pal, -38, -60); lantern(ctx, pal, 38, -60)
            }
        }, ...bannerXs.map((bx, i) => ({
            // a company banner on its pole
            x: l.x + bx, y: baseY + 26, box: [-14, -112, 26, 4],
            paint(ctx, pal) {
                const [c1, c2] = banners[i % banners.length]
                line(ctx, [[0, 0], [0, -104]], pal.ink, 3.6); line(ctx, [[0, 0], [0, -104]], pal.woodDark, 2)
                line(ctx, [[0, -98], [20, -98]], pal.woodDark, 2)
                inkPoly(ctx, [[2, -98], [20, -98], [20, -58], [11, -64], [2, -58]], c1, pal.ink, { line: 1.3 })
                ctx.fillStyle = c2
                if (i % 3 === 0) { ctx.beginPath(); ctx.arc(11, -84, 5, 0, Math.PI * 2); ctx.fill() } else if (i % 3 === 1) { inkPoly(ctx, [[11, -92], [16, -80], [6, -80]], c2, null) } else { ctx.fillRect(6, -88, 10, 8) }
            }
        })), {
            // the board of open roles
            x: l.x + 72, y: baseY + 44, box: [-40, -78, 40, 6],
            paint(ctx, pal) {
                const ink = pal.ink
                ;[-28, 28].forEach((x) => line(ctx, [[x, 0], [x, -70]], pal.woodDark, 3))
                inkRect(ctx, -34, -70, 68, 44, pal.woodLight, ink, { line: 1.5 })
                const r = rng(861)
                for (let i = 0; i < 6; i++) {
                    const cx = -24 + (i % 3) * 22, cy = -64 + Math.floor(i / 3) * 19
                    ctx.save(); ctx.translate(cx + 8, cy + 7); ctx.rotate((r() - 0.5) * 0.2)
                    inkRect(ctx, -8, -7, 16, 14, i % 2 ? "#FAF7EF" : "#FFF3B8", ink, { line: 0.8 })
                    for (let k = 0; k < 3; k++) line(ctx, [[-5, -3 + k * 3], [5, -3 + k * 3]], withAlpha(ink, 0.45), 0.7)
                    ctx.fillStyle = pal.flag[i % pal.flag.length]; ctx.beginPath(); ctx.arc(0, -8, 1.6, 0, Math.PI * 2); ctx.fill()
                    ctx.restore()
                }
                sign(ctx, pal, 0, -72, "NOW HIRING", "#FFF6D8", 9)
            }
        }, {
            // a woodpile against the end wall
            x: l.x + 148, y: baseY + 14, box: [-30, -40, 30, 6],
            paint(ctx, pal) {
                for (let row = 0; row < 3; row++) for (let i = 0; i < 5 - row; i++) inkEllipse(ctx, -20 + i * 10 + row * 5, -6 - row * 9, 5.4, 5, pal.woodLight, pal.ink, 1)
            }
        }],
        decals: [(ctx, pal) => {
            flagstones(ctx, pal, l.x, l.y + 30, 150, 42, 862)
        }],
        footprints: [{ x: l.x - w / 2 - 10, y: baseY - 40, w: w + 50, h: 42 }, { circle: true, x: l.x + 148, y: baseY + 8, r: 22 }, { x: l.x + 40, y: baseY + 38, w: 64, h: 10 }],
        lights: [{ x: l.x - 84, y: baseY - 42, r: 70 }, { x: l.x + 84, y: baseY - 42, r: 70 }, { x: l.x - 38, y: baseY - 58, r: 55 }, { x: l.x + 38, y: baseY - 58, r: 55 }],
        anchors: { guildBanners: bannerXs.map((bx) => ({ x: l.x + bx + 11, y: baseY + 26 - 78 })), guildSmoke: { x: l.x - 30, y: baseY - h - 72 } }
    }
}

// ---- the birch edge: the sauna (coming soon: Unplug) ------------------------------------
// A log sauna with a stovepipe, towels drying on the porch rail, and a hot
// spring steaming in its ring of stones beside it.

function sauna(l) {
    const baseY = l.y - 20
    const pool = { x: l.x + 110, y: l.y + 30, rx: 62, ry: 30 }
    return {
        sprites: [{
            x: l.x - 20, y: baseY, box: [-80, -150, 90, 8],
            paint(ctx, pal) {
                const ink = pal.ink
                inkPoly(ctx, [[56, 0], [74, -9], [74, -58], [56, -50]], mix(pal.wood, "#000000", 0.3), ink)
                logWall(ctx, pal, -56, 56, 0, -50, pal.wood)
                turfRoof(ctx, pal, [[-66, -92], [60, -92], [80, -46], [-70, -46]], { snowy: false, seed: 871 })
                snowOn(ctx, pal, -66, 60, -92, 5)
                // stovepipe
                inkRect(ctx, 26, -118, 10, 30, pal.metal, ink, { line: 1.3 })
                inkRect(ctx, 23, -122, 16, 6, pal.stoneDark, ink, { line: 1.1 })
                // a red door with a heart cut out, and a small window
                inkRect(ctx, -18, -44, 26, 44, pal.falu, ink, { line: 1.5 })
                ctx.fillStyle = pal.night ? pal.windowLit : "#3A2A22"
                ctx.beginPath(); ctx.moveTo(-5, -30); ctx.bezierCurveTo(-12, -36, -9, -42, -5, -38); ctx.bezierCurveTo(-1, -42, 2, -36, -5, -30); ctx.fill()
                inkRect(ctx, 20, -40, 20, 16, pal.night ? pal.windowLit : pal.window, ink, { line: 1.2 })
                line(ctx, [[30, -40], [30, -24]], pal.woodDark, 1.4)
                // the porch rail with towels
                line(ctx, [[-56, -22], [-86, -18]], pal.woodDark, 2.4)
                line(ctx, [[-86, -18], [-86, 0]], pal.woodDark, 2.4)
                ;[["#EAF2F4", -78], ["#E8894A", -66]].forEach(([c, x]) => inkPoly(ctx, [[x - 5, -21], [x + 5, -22], [x + 6, -6], [x - 4, -5]], c, ink, { line: 0.9 }))
            }
        }, {
            // the bucket and ladle by the spring
            x: pool.x - 70, y: pool.y + 16, box: [-14, -30, 14, 4],
            paint(ctx, pal) {
                inkPoly(ctx, [[-9, 0], [-10, -14], [10, -14], [9, 0]], pal.wood, pal.ink, { line: 1.1 })
                line(ctx, [[-10, -4], [10, -4]], pal.metal, 1.6)
                line(ctx, [[2, -14], [10, -26]], pal.woodDark, 1.8)
            }
        }],
        decals: [(ctx, pal) => {
            flagstones(ctx, pal, l.x - 10, l.y + 16, 86, 26, 872)
            // the hot spring: a ring of stones round steaming blue water
            ctx.save()
            blobPath(ctx, pool.x, pool.y, pool.rx + 12, pool.ry + 9, 873, 0.12, 22)
            ctx.fillStyle = pal.stoneDark; ctx.fill()
            blobPath(ctx, pool.x, pool.y, pool.rx, pool.ry, 874, 0.1, 22)
            const g = ctx.createRadialGradient(pool.x, pool.y, 4, pool.x, pool.y, pool.rx)
            g.addColorStop(0, pal.night ? "#2F6E75" : "#7FD3D0"); g.addColorStop(1, pal.night ? "#1F4A55" : "#4FA9B6")
            ctx.fillStyle = g; ctx.fill(); ctx.strokeStyle = pal.ink; ctx.lineWidth = 1.2; ctx.stroke()
            ctx.restore()
            const r = rng(875)
            for (let i = 0; i < 16; i++) {
                const a = (i / 16) * Math.PI * 2
                inkEllipse(ctx, pool.x + Math.cos(a) * (pool.rx + 6), pool.y + Math.sin(a) * (pool.ry + 5), 6 + r() * 4, 4 + r() * 2, r() < 0.5 ? pal.stone : pal.stoneLight, pal.ink, 0.9)
            }
        }],
        // the pool is water: walk round it
        footprints: [{ x: l.x - 80, y: baseY - 30, w: 150, h: 32 }, { circle: true, x: pool.x - 70, y: pool.y + 14, r: 10 }, { circle: true, x: pool.x - 30, y: pool.y, r: 30 }, { circle: true, x: pool.x + 30, y: pool.y, r: 30 }],
        lights: [{ x: l.x + 10, y: baseY - 32, r: 60 }],
        walkways: [],
        anchors: { saunaSteam: [{ x: l.x + 11, y: baseY - 122 }, { x: pool.x - 20, y: pool.y - 6 }, { x: pool.x + 18, y: pool.y + 2 }] }
    }
}

// ---- the birch edge: Bottle cove (Feedback) ---------------------------------------
// A driftwood rack of bottles on the shingle, more bottles in the sand, a
// rowing boat pulled up, and the sign: throw one of your own.

function bottlecove(l) {
    const baseY = l.y - 10
    const glass = ["#6FA86A", "#C98A3A", "#5A8FC0", "#DDEFEF", "#8A6FB0"]
    return {
        sprites: [{
            x: l.x - 20, y: baseY, box: [-70, -100, 70, 8],
            paint(ctx, pal) {
                const ink = pal.ink, drift = mix(pal.stoneLight, pal.woodLight, 0.4)
                ;[-58, 58].forEach((x, i) => { line(ctx, [[x, 0], [x + (i ? 2 : -2), -86]], ink, 6); line(ctx, [[x, 0], [x + (i ? 2 : -2), -86]], drift, 4) })
                const r = rng(881)
                ;[-18, -44, -70].forEach((y, row) => {
                    line(ctx, [[-64, y], [64, y]], ink, 5); line(ctx, [[-64, y], [64, y]], drift, 3.2)
                    for (let i = 0; i < 7; i++) {
                        const bx = -50 + i * 16 + (r() - 0.5) * 4, c = glass[Math.floor(r() * glass.length)], tall = 14 + r() * 5
                        inkPoly(ctx, [[bx - 4, y - 1], [bx + 4, y - 1], [bx + 4, y - tall + 6], [bx + 1.8, y - tall + 2], [bx + 1.8, y - tall], [bx - 1.8, y - tall], [bx - 1.8, y - tall + 2], [bx - 4, y - tall + 6]], withAlpha(c, 0.85), ink, { line: 0.9 })
                        if (r() < 0.6) { ctx.fillStyle = "#F3E9D2"; ctx.fillRect(bx - 1.6, y - tall + 7, 3.2, 6) }
                        inkRect(ctx, bx - 1.6, y - tall - 3, 3.2, 3, pal.woodDark, null)
                    }
                    if (row === 2) sign(ctx, pal, 0, y - 22, "BOTTLES", "#FFF6D8", 10)
                })
            }
        }, {
            // the rowing boat, pulled up
            x: l.x + 90, y: l.y + 34, box: [-56, -36, 56, 8],
            paint(ctx, pal) {
                const ink = pal.ink
                ctx.save(); ctx.rotate(-0.18)
                ctx.beginPath(); ctx.moveTo(-50, -10); ctx.quadraticCurveTo(-40, 8, 0, 8); ctx.quadraticCurveTo(40, 8, 52, -14); ctx.lineTo(-50, -10); ctx.closePath()
                ctx.fillStyle = pal.falu; ctx.fill(); ctx.strokeStyle = ink; ctx.lineWidth = 1.6; ctx.stroke()
                ctx.beginPath(); ctx.moveTo(-46, -10); ctx.quadraticCurveTo(0, -4, 48, -14); ctx.lineTo(46, -18); ctx.quadraticCurveTo(0, -10, -46, -14); ctx.closePath()
                ctx.fillStyle = pal.woodLight; ctx.fill(); ctx.stroke()
                ;[-20, 10].forEach((x) => line(ctx, [[x, -12], [x + 2, -6]], pal.woodDark, 3))
                line(ctx, [[-10, -8], [30, 10]], pal.woodDark, 2.4)
                ctx.restore()
            }
        }],
        decals: [(ctx, pal) => {
            // bottles in the shingle, one with its note unrolled
            const r = rng(882)
            for (let i = 0; i < 12; i++) {
                const x = l.x - 100 + r() * 200, y = l.y + 20 + r() * 70, a = r() * Math.PI
                ctx.save(); ctx.translate(x, y); ctx.rotate(a)
                inkRect(ctx, -8, -3, 13, 6, withAlpha(glass[i % glass.length], 0.85), pal.ink, { line: 0.8 })
                inkRect(ctx, 5, -1.6, 5, 3.2, withAlpha(glass[i % glass.length], 0.85), pal.ink, { line: 0.6 })
                ctx.restore()
            }
            ctx.save(); ctx.translate(l.x - 40, l.y + 64); ctx.rotate(0.1)
            inkRect(ctx, -12, -8, 24, 16, "#F3E9D2", pal.ink, { line: 0.9 })
            for (let k = 0; k < 3; k++) line(ctx, [[-8, -3 + k * 4], [8, -3 + k * 4]], withAlpha(pal.ink, 0.45), 0.7)
            ctx.restore()
        }],
        footprints: [{ x: l.x - 84, y: baseY - 12, w: 130, h: 14 }, { x: l.x + 38, y: l.y + 20, w: 104, h: 18 }],
        lights: [],
        anchors: { driftBottles: [{ x: l.x + 60, y: l.y + 170 }, { x: l.x - 30, y: l.y + 210 }, { x: l.x + 150, y: l.y + 140 }] }
    }
}

// ---- the fjord: the world port (Countries) ------------------------------------------------
// A cobbled quay on the fjord's mouth: a falu-red warehouse, a row of flags,
// a big brass globe on a compass rose, a departures board, and a ship in
// from somewhere far away.

function worldport(l) {
    const baseY = l.y - 30
    const flags = [["#BA0C2F", "#FFFFFF", "#00205B"], ["#0055A4", "#FFFFFF", "#EF4135"], ["#BC002D", "#FFFFFF"], ["#009C3B", "#FFDF00"], ["#000000", "#DD0000", "#FFCE00"], ["#006AA7", "#FECC00"], ["#FF9933", "#FFFFFF", "#138808"], ["#D52B1E", "#FFFFFF"]]
    const poles = flags.map((_, i) => ({ x: l.x - 150 + i * 36, y: l.y + 96 }))
    return {
        sprites: [{
            // the warehouse
            x: l.x - 60, y: baseY - 20, box: [-90, -160, 110, 8],
            paint(ctx, pal) {
                const ink = pal.ink
                inkPoly(ctx, [[70, 0], [96, -12], [96, -74], [70, -64]], pal.faluDark, ink)
                inkRect(ctx, -70, -64, 140, 64, pal.falu, ink, { line: 1.8 })
                for (let x = -62; x < 70; x += 8) line(ctx, [[x, -64], [x, 0]], mix(pal.falu, "#000000", 0.22), 1, 0.7)
                inkPoly(ctx, [[-78, -62], [0, -122], [78, -62]], pal.roofDark, ink, { line: 1.8 })
                inkPoly(ctx, [[0, -122], [26, -134], [104, -74], [78, -62]], mix(pal.roofDark, "#000000", 0.2), ink)
                snowOn(ctx, pal, -60, 60, -70, 3)
                // white trim, loading doors, a hoist beam
                ctx.strokeStyle = pal.cloth; ctx.lineWidth = 2.6
                ctx.strokeRect(-22, -52, 44, 52); ctx.beginPath(); ctx.moveTo(0, -52); ctx.lineTo(0, 0); ctx.stroke()
                inkRect(ctx, -12, -100, 24, 22, pal.woodDark, ink, { line: 1.2 }); ctx.strokeRect(-12, -100, 24, 22)
                line(ctx, [[0, -108], [0, -122]], pal.woodDark, 3); line(ctx, [[0, -112], [24, -112]], pal.woodDark, 3)
                line(ctx, [[22, -112], [22, -90]], ink, 1)
                ;[-52, 52].forEach((x) => inkRect(ctx, x - 9, -46, 18, 16, pal.night ? pal.windowLit : pal.window, ink, { line: 1.2 }))
                sign(ctx, pal, 0, -60, "WORLD PORT", "#FFF6D8", 11)
            }
        }, {
            // the globe on its plinth
            x: l.x + 80, y: l.y + 30, box: [-40, -110, 40, 6],
            paint(ctx, pal) {
                const ink = pal.ink, brass = pal.night ? "#8A7440" : "#C9A04A"
                inkPoly(ctx, [[-20, 0], [20, 0], [14, -26], [-14, -26]], pal.stone, ink, { line: 1.5 })
                inkRect(ctx, -18, -30, 36, 6, pal.stoneLight, ink, { line: 1.2 })
                line(ctx, [[0, -30], [0, -40]], brass, 3)
                ctx.beginPath(); ctx.arc(0, -70, 32, Math.PI * 0.65, Math.PI * 2.35); ctx.strokeStyle = ink; ctx.lineWidth = 4.4; ctx.stroke(); ctx.strokeStyle = brass; ctx.lineWidth = 2.6; ctx.stroke()
                inkEllipse(ctx, 0, -70, 26, 26, pal.night ? "#2E5566" : "#6FB3C8", ink, 1.6)
                ctx.save(); ctx.beginPath(); ctx.arc(0, -70, 26, 0, Math.PI * 2); ctx.clip()
                ctx.fillStyle = pal.night ? "#4E6A44" : "#8FB061"
                ;[[-12, -80, 10, 8], [6, -64, 12, 9], [-2, -90, 7, 4], [14, -80, 6, 8], [-16, -60, 6, 7]].forEach(([x, y, rx, ry]) => { ctx.beginPath(); ctx.ellipse(x, y, rx, ry, 0.4, 0, Math.PI * 2); ctx.fill() })
                ctx.strokeStyle = withAlpha(pal.cloth, 0.6); ctx.lineWidth = 0.8
                ;[-12, 0, 12].forEach((y) => { ctx.beginPath(); ctx.ellipse(0, -70 + y, Math.sqrt(26 * 26 - y * y), 3, 0, 0, Math.PI * 2); ctx.stroke() })
                ctx.beginPath(); ctx.ellipse(0, -70, 10, 26, 0, 0, Math.PI * 2); ctx.stroke()
                ctx.restore()
                ctx.fillStyle = "rgba(255,255,255,0.4)"; ctx.beginPath(); ctx.ellipse(-9, -80, 7, 4, -0.5, 0, Math.PI * 2); ctx.fill()
            }
        }, {
            // the departures board
            x: l.x - 150, y: l.y + 30, box: [-50, -96, 50, 6],
            paint(ctx, pal) {
                const ink = pal.ink
                ;[-38, 38].forEach((x) => line(ctx, [[x, 0], [x, -90]], pal.metal, 3.4))
                inkRect(ctx, -46, -92, 92, 62, pal.night ? "#141C1E" : "#23302F", ink, { line: 1.5 })
                ctx.fillStyle = "#F2C14E"; ctx.font = `700 8px ${HAND}`; ctx.textAlign = "left"
                ;["OSLO", "TOKYO", "LIMA", "CAIRO", "PERTH"].forEach((city, i) => {
                    ctx.fillStyle = "#F2C14E"; ctx.fillText(city, -40, -80 + i * 11)
                    ctx.fillStyle = "#9FE0B0"; ctx.fillText(["ON TIME", "BOARDING", "08:40", "DELAYED", "11:15"][i], 4, -80 + i * 11)
                })
                ctx.textAlign = "center"
                sign(ctx, pal, 0, -94, "DEPARTURES", "#FFF6D8", 9)
            }
        }, ...poles.map((p, i) => ({
            // the flags of the world
            x: p.x, y: p.y, box: [-6, -96, 28, 4],
            // the pole; its flag flies in the motion layer (ambient_north.js)
            paint(ctx, pal) {
                line(ctx, [[0, 0], [0, -90]], pal.ink, 3.2); line(ctx, [[0, 0], [0, -90]], pal.cloth, 1.6)
                inkEllipse(ctx, 0, -91, 2.4, 2.4, "#C9A04A", pal.ink, 0.8)
            }
        })), {
            // a ship at the quay's end, in from somewhere far away
            x: l.x + 300, y: l.y + 90, box: [-110, -170, 110, 20],
            paint(ctx, pal) {
                const ink = pal.ink
                ctx.beginPath(); ctx.moveTo(-100, -18); ctx.lineTo(96, -24); ctx.quadraticCurveTo(84, 6, 40, 10); ctx.lineTo(-76, 10); ctx.quadraticCurveTo(-96, 4, -100, -18); ctx.closePath()
                ctx.fillStyle = pal.night ? "#1F2E3A" : "#2F4A63"; ctx.fill(); ctx.strokeStyle = ink; ctx.lineWidth = 1.8; ctx.stroke()
                line(ctx, [[-94, -8], [90, -14]], pal.falu, 3)
                inkPoly(ctx, [[-100, -18], [96, -24], [92, -30], [-96, -26]], pal.woodLight, ink, { line: 1.3 })
                ;[-60, 0, 54].forEach((x, i) => {
                    line(ctx, [[x, -26], [x, -150 + i * 12]], pal.woodDark, 3)
                    ;[-120 + i * 12, -84 + i * 10, -54].forEach((y, k) => { if (k < 3) line(ctx, [[x - 26 + k * 4, y], [x + 26 - k * 4, y]], pal.woodDark, 2) })
                })
                ;[-60, 0, 54].forEach((x, i) => inkPoly(ctx, [[x - 22, -116 + i * 12], [x + 22, -116 + i * 12], [x + 20, -90 + i * 10], [x - 20, -90 + i * 10]], pal.cloth, ink, { line: 1.1 }))
                ;[-100, 96].forEach((x) => line(ctx, [[x, -24], [x < 0 ? -60 : 54, -148 + (x < 0 ? 0 : 24)]], withAlpha(ink, 0.6), 0.9))
                inkPoly(ctx, [[0, -152], [18, -148], [0, -144]], pal.flag[0], ink, { line: 0.8 })
            }
        }, {
            x: l.x + 196, y: l.y + 60, box: [-10, -18, 10, 4],
            paint(ctx, pal) { inkRect(ctx, -6, -14, 12, 14, pal.metal, pal.ink, { line: 1.2 }); inkEllipse(ctx, 0, -14, 7, 3, pal.stoneDark, pal.ink, 1) }
        }, barrel(l.x + 10, l.y + 100), barrel(l.x + 30, l.y + 108, 0.9), crate(l.x - 30, l.y + 110, 16, "SEA")],
        decals: [(ctx, pal) => {
            // the cobbled quay, out to the water's edge
            cobbles(ctx, pal, l.x + 40, l.y + 50, 250, 90, 891)
            // a compass rose under the globe
            ctx.save(); ctx.translate(l.x + 80, l.y + 40)
            inkEllipse(ctx, 0, 0, 58, 22, withAlpha(pal.stoneLight, 0.9), pal.ink, 1.2)
            ;[0, 1, 2, 3].forEach((k) => {
                const a = (k / 4) * Math.PI * 2
                inkPoly(ctx, [[Math.cos(a) * 52, Math.sin(a) * 19], [Math.cos(a + 0.35) * 12, Math.sin(a + 0.35) * 5], [Math.cos(a - 0.35) * 12, Math.sin(a - 0.35) * 5]], k % 2 ? pal.stoneDark : pal.falu, pal.ink, { line: 0.9 })
            })
            ctx.restore()
        }],
        footprints: [{ x: l.x - 136, y: baseY - 60, w: 190, h: 44 }, { circle: true, x: l.x + 80, y: l.y + 24, r: 20 }, { x: l.x - 196, y: l.y + 22, w: 92, h: 10 }, { circle: true, x: l.x + 196, y: l.y + 56, r: 7 }],
        lights: [{ x: l.x - 112, y: baseY - 60, r: 60 }, { x: l.x - 8, y: baseY - 60, r: 60 }, { x: l.x - 150, y: l.y - 30, r: 70 }],
        anchors: { portFlags: poles.map((p, i) => ({ x: p.x, y: p.y, bands: flags[i] })), portShip: { x: l.x + 300, y: l.y + 90 } }
    }
}

// ---- the fjord: the ice-fishing hut (coming soon: Today's Catch) --------------------------
// A little red hut on skids out on the ice, smoke from its stovepipe, and a
// hole in the ice with a rod waiting over it.

function icehut(l) {
    const baseY = l.y - 12
    const hole = { x: l.x + 58, y: l.y + 26 }
    return {
        sprites: [{
            x: l.x - 16, y: baseY, box: [-50, -108, 56, 8],
            paint(ctx, pal) {
                const ink = pal.ink
                line(ctx, [[-44, 2], [44, 2]], ink, 4); line(ctx, [[-44, 2], [44, 2]], pal.metal, 2)
                inkPoly(ctx, [[34, -2], [46, -9], [46, -50], [34, -44]], pal.faluDark, ink)
                inkRect(ctx, -34, -44, 68, 42, pal.falu, ink, { line: 1.7 })
                for (let x = -28; x < 34; x += 7) line(ctx, [[x, -44], [x, -2]], mix(pal.falu, "#000000", 0.2), 1, 0.7)
                inkPoly(ctx, [[-40, -42], [0, -72], [40, -42]], pal.woodDark, ink, { line: 1.6 })
                snowOn(ctx, pal, -38, 38, -45, 6)
                ctx.fillStyle = pal.snowLight; ctx.beginPath(); ctx.moveTo(-36, -44); ctx.lineTo(0, -70); ctx.lineTo(36, -44); ctx.lineTo(0, -60); ctx.closePath(); ctx.fill()
                inkRect(ctx, 16, -92, 7, 30, pal.metal, ink, { line: 1.1 })
                inkRect(ctx, -12, -34, 18, 32, pal.woodDark, ink, { line: 1.3 })
                inkRect(ctx, 12, -34, 14, 12, pal.night ? pal.windowLit : pal.window, ink, { line: 1.1 })
                ctx.strokeStyle = pal.cloth; ctx.lineWidth = 1.6; ctx.strokeRect(-34, -44, 68, 42)
            }
        }, {
            // the rod on its stand over the hole, and a stool
            x: hole.x - 16, y: hole.y + 6, box: [-24, -56, 60, 6],
            paint(ctx, pal) {
                const ink = pal.ink
                line(ctx, [[-6, 0], [0, -20]], pal.woodDark, 2); line(ctx, [[6, 0], [0, -20]], pal.woodDark, 2)
                line(ctx, [[-10, -14], [34, -46]], ink, 2.8); line(ctx, [[-10, -14], [34, -46]], pal.woodLight, 1.6)
                line(ctx, [[34, -46], [18, -8]], withAlpha(ink, 0.7), 0.8)
                inkRect(ctx, -22, -14, 14, 4, pal.wood, ink, { line: 1 }); line(ctx, [[-20, -10], [-20, 0]], pal.woodDark, 1.6); line(ctx, [[-10, -10], [-10, 0]], pal.woodDark, 1.6)
            }
        }, barrel(l.x - 70, l.y + 16, 0.7)],
        decals: [(ctx, pal) => {
            // the hole: slush, then dark water
            inkEllipse(ctx, hole.x, hole.y, 16, 8, withAlpha(pal.snowShade, 0.95), withAlpha(pal.iceCrack, 0.8), 1.2)
            inkEllipse(ctx, hole.x, hole.y, 11, 5, pal.night ? "#0F2430" : "#2A5A70", pal.ink, 1)
            // footprints to the door
            ctx.fillStyle = withAlpha(pal.snowDeep, 0.8)
            for (let i = 0; i < 9; i++) { ctx.beginPath(); ctx.ellipse(l.x - 10 - i * 14, l.y + 22 + (i % 2) * 7, 2, 3, 0.3, 0, Math.PI * 2); ctx.fill() }
        }],
        footprints: [{ x: l.x - 60, y: baseY - 20, w: 104, h: 22 }, { circle: true, x: hole.x, y: hole.y, r: 12 }],
        lights: [{ x: l.x, y: baseY - 26, r: 55 }],
        anchors: { hutSmoke: { x: l.x + 3, y: baseY - 94 }, fishingHole: hole }
    }
}

// ---- the snowfields: the cairn (coming soon: Time Capsule) --------------------------------
// A tall cairn of rounded stones with letters tucked between them, smaller
// cairns around it, and a post with a slot for more.

function cairn(l) {
    const baseY = l.y - 6
    const stack = (s, seed) => (ctx, pal) => {
        const r = rng(seed)
        let y = 0, w = s
        for (let i = 0; i < 7 && w > s * 0.25; i++) {
            const h = w * (0.45 + r() * 0.15), x = (r() - 0.5) * s * 0.18
            inkEllipse(ctx, x, y - h / 2, w / 2, h / 2, r() < 0.5 ? pal.stone : pal.stoneLight, pal.ink, 1.3)
            ctx.fillStyle = withAlpha(pal.stoneDark, 0.6); ctx.beginPath(); ctx.ellipse(x + w * 0.15, y - h * 0.35, w * 0.25, h * 0.2, 0, 0, Math.PI * 2); ctx.fill()
            ctx.fillStyle = pal.snowLight; ctx.beginPath(); ctx.ellipse(x - w * 0.05, y - h * 0.9, w * 0.32, h * 0.14, 0, 0, Math.PI * 2); ctx.fill()
            y -= h * 0.85
            w *= 0.78
        }
    }
    return {
        sprites: [{
            x: l.x, y: baseY, box: [-40, -120, 40, 6],
            paint(ctx, pal) {
                stack(62, 901)(ctx, pal)
                // letters tucked between the stones
                ;[[-16, -30, 0.3], [12, -52, -0.4], [-6, -74, 0.2]].forEach(([x, y, a]) => {
                    ctx.save(); ctx.translate(x, y); ctx.rotate(a)
                    inkRect(ctx, -7, -4.5, 14, 9, "#FAF7EF", pal.ink, { line: 0.8 })
                    line(ctx, [[-7, -4.5], [0, 1], [7, -4.5]], withAlpha(pal.ink, 0.6), 0.7)
                    ctx.fillStyle = "#C8453A"; ctx.beginPath(); ctx.arc(0, 1, 1.6, 0, Math.PI * 2); ctx.fill()
                    ctx.restore()
                })
            }
        }, ...[[-70, 30, 28, 902], [74, 22, 24, 903], [30, 60, 20, 904]].map(([dx, dy, s, sd]) => ({
            x: l.x + dx, y: l.y + dy, box: [-s, -s * 2.2, s, 4], paint(ctx, pal) { stack(s, sd)(ctx, pal) }
        })), {
            // the letter post
            x: l.x + 60, y: l.y - 30, box: [-16, -60, 16, 4],
            paint(ctx, pal) {
                line(ctx, [[0, 0], [0, -40]], pal.woodDark, 3.4)
                inkRect(ctx, -12, -58, 24, 18, pal.night ? "#8A7440" : "#C9A04A", pal.ink, { line: 1.3 })
                line(ctx, [[-7, -50], [7, -50]], pal.ink, 2)
                snowOn(ctx, pal, -12, 12, -58, 3)
            }
        }],
        decals: [(ctx, pal) => {
            ctx.fillStyle = withAlpha(pal.snowDeep, 0.7)
            for (let i = 0; i < 24; i++) { const a = (i / 24) * Math.PI * 2; ctx.beginPath(); ctx.ellipse(l.x + Math.cos(a) * 44, l.y + Math.sin(a) * 18, 2, 2.8, a, 0, Math.PI * 2); ctx.fill() }
        }],
        footprints: [{ circle: true, x: l.x, y: baseY - 6, r: 30 }, { circle: true, x: l.x - 70, y: l.y + 26, r: 14 }, { circle: true, x: l.x + 74, y: l.y + 18, r: 12 }, { circle: true, x: l.x + 30, y: l.y + 56, r: 10 }, { circle: true, x: l.x + 60, y: l.y - 32, r: 8 }],
        lights: [],
        anchors: { cairn: { x: l.x, y: baseY - 60 } }
    }
}

// ---- the snowfields: the seed vault (coming soon: Legacy) ---------------------------------
// A concrete wedge jutting out of the snow with a tall steel door, and above
// it a panel of mirrored tiles that glows green and blue after dark.

function seedvault(l) {
    const baseY = l.y + 10
    return {
        sprites: [{
            x: l.x, y: baseY, box: [-120, -200, 110, 10],
            paint(ctx, pal) {
                const ink = pal.ink, concrete = pal.night ? "#6A6E6C" : "#B9B8B0", concreteDark = pal.night ? "#4E5250" : "#8F8E86"
                // the snowy hill it is cut into, shaded blue on its far side
                ctx.save()
                ctx.beginPath(); ctx.moveTo(-120, 0); ctx.quadraticCurveTo(-96, -120, -10, -150); ctx.quadraticCurveTo(84, -150, 110, 0); ctx.closePath()
                const hill = ctx.createLinearGradient(-120, 0, 110, 0)
                hill.addColorStop(0, pal.snowLight); hill.addColorStop(0.6, pal.snow); hill.addColorStop(1, pal.snowShade)
                ctx.fillStyle = hill; ctx.fill()
                ctx.strokeStyle = withAlpha(pal.snowDeep, 0.9); ctx.lineWidth = 2; ctx.stroke()
                ctx.clip()
                ;[[-70, -40, 12], [60, -70, 10], [-30, -110, 8]].forEach(([x, y, s]) => { ctx.fillStyle = pal.stoneDark; ctx.beginPath(); ctx.ellipse(x, y, s, s * 0.55, 0, Math.PI, 0); ctx.fill() })
                ctx.restore()
                // the wedge: its side, then its face
                inkPoly(ctx, [[22, 0], [58, -18], [58, -150], [22, -176]], concreteDark, ink, { line: 1.7 })
                inkPoly(ctx, [[-22, 0], [22, 0], [22, -176], [-22, -150]], concrete, ink, { line: 1.8 })
                ctx.save()
                ctx.beginPath(); ctx.moveTo(-22, 0); ctx.lineTo(22, 0); ctx.lineTo(22, -176); ctx.lineTo(-22, -150); ctx.closePath(); ctx.clip()
                hatch(ctx, [-22, -176, 44, 176], concreteDark, { seed: 911, angle: -1.3, gap: 6, alpha: 0.35 })
                ctx.restore()
                // the glowing panel of mirrored tiles
                const glow = pal.night
                const r = rng(912)
                for (let row = 0; row < 7; row++) for (let col = 0; col < 3; col++) {
                    const x = -16 + col * 11, y = -142 + row * 11 + (col % 2) * 3
                    const c = glow ? (r() < 0.5 ? "#5FE0C0" : "#6FA8FF") : (r() < 0.5 ? "#D8E6E8" : "#A9C3CA")
                    inkPoly(ctx, [[x, y], [x + 9, y + 4], [x, y + 9]], c, withAlpha(ink, 0.6), { line: 0.7 })
                }
                // the door
                inkRect(ctx, -12, -58, 24, 58, pal.metal, ink, { line: 1.5 })
                line(ctx, [[0, -58], [0, 0]], withAlpha(ink, 0.6), 1)
                inkEllipse(ctx, 5, -30, 1.8, 1.8, pal.stoneLight, ink, 0.6)
                snowOn(ctx, pal, -24, 58, -176, 6)
            }
        }],
        decals: [(ctx, pal) => {
            // a ploughed apron of packed snow in front
            ctx.save()
            blobPath(ctx, l.x, l.y + 40, 90, 36, 913, 0.12, 20)
            ctx.fillStyle = withAlpha(pal.snowTrail, 0.95); ctx.fill(); ctx.restore()
            line(ctx, [[l.x - 60, l.y + 30], [l.x + 60, l.y + 34]], withAlpha(pal.snowDeep, 0.8), 2)
            line(ctx, [[l.x - 60, l.y + 50], [l.x + 60, l.y + 54]], withAlpha(pal.snowDeep, 0.8), 2)
        }],
        footprints: [{ x: l.x - 110, y: baseY - 60, w: 216, h: 62 }],
        lights: [{ x: l.x, y: baseY - 110, r: 90 }],
        anchors: { vaultGlow: { x: l.x, y: baseY - 110 } }
    }
}

// ---- the snowfields: Aurora point (Dating) ------------------------------------------------
// A bench for two on the clifftop, facing north, with a blanket over it and a
// lantern either side. The northern lights themselves are in the motion layer.

function aurora(l) {
    const baseY = l.y - 10
    return {
        sprites: [{
            x: l.x, y: baseY, box: [-70, -70, 70, 8],
            paint(ctx, pal) {
                const ink = pal.ink
                // seen from behind: legs, the seat, the back, a blanket over it
                ;[-44, 44].forEach((x) => { line(ctx, [[x, 0], [x, -20]], pal.woodDark, 3); line(ctx, [[x - 4, 0], [x - 4, -44]], pal.woodDark, 3) })
                inkRect(ctx, -52, -24, 104, 8, pal.woodLight, ink, { line: 1.3 })
                ;[-44, -34].forEach((y) => inkRect(ctx, -54, y, 108, 6, pal.wood, ink, { line: 1.2 }))
                ctx.beginPath(); ctx.moveTo(-10, -44); ctx.lineTo(30, -44); ctx.lineTo(34, -12); ctx.lineTo(-6, -12); ctx.closePath()
                ctx.fillStyle = pal.falu; ctx.fill(); ctx.strokeStyle = ink; ctx.lineWidth = 1.2; ctx.stroke()
                for (let x = -4; x < 32; x += 8) line(ctx, [[x, -44], [x + 2, -12]], withAlpha(pal.cloth, 0.7), 1.4)
                snowOn(ctx, pal, -54, -12, -44, 3)
                // a heart carved in the back rail
                ctx.strokeStyle = withAlpha(ink, 0.7); ctx.lineWidth = 1
                ctx.beginPath(); ctx.moveTo(-30, -36); ctx.bezierCurveTo(-36, -42, -30, -46, -30, -41); ctx.bezierCurveTo(-30, -46, -24, -42, -30, -36); ctx.stroke()
            }
        }, ...[-90, 90].map((dx) => ({
            x: l.x + dx, y: baseY + 4, box: [-12, -70, 12, 4],
            paint(ctx, pal) { line(ctx, [[0, 0], [0, -52]], pal.woodDark, 3); lantern(ctx, pal, 0, -56); snowOn(ctx, pal, -7, 7, -67, 2) }
        })), ...[[-150, -40, 16], [140, -30, 20], [170, 10, 12]].map(([dx, dy, s]) => ({
            // rocks at the cliff's edge
            x: l.x + dx, y: l.y + dy, box: [-s * 1.4, -s * 1.3, s * 1.4, 4],
            paint(ctx, pal) {
                ctx.beginPath(); ctx.moveTo(-s, 0); ctx.lineTo(-s * 0.7, -s * 0.8); ctx.lineTo(0, -s * 1.05); ctx.lineTo(s * 0.8, -s * 0.6); ctx.lineTo(s, 0); ctx.closePath()
                ctx.fillStyle = pal.stoneDark; ctx.fill(); ctx.strokeStyle = pal.ink; ctx.lineWidth = 1.4; ctx.stroke()
                ctx.fillStyle = pal.snowLight; ctx.beginPath(); ctx.moveTo(-s * 0.7, -s * 0.8); ctx.lineTo(0, -s * 1.05); ctx.lineTo(s * 0.6, -s * 0.66); ctx.quadraticCurveTo(0, -s * 0.7, -s * 0.7, -s * 0.8); ctx.fill()
            }
        }))],
        decals: [(ctx, pal) => {
            // the trodden viewpoint, and the cliff edge beyond it
            ctx.save()
            blobPath(ctx, l.x, l.y + 6, 120, 40, 921, 0.1, 22)
            ctx.fillStyle = withAlpha(pal.snowTrail, 0.95); ctx.fill(); ctx.restore()
            ctx.save()
            ctx.strokeStyle = withAlpha(pal.stoneDark, 0.9); ctx.lineWidth = 5; ctx.lineCap = "round"
            ctx.beginPath(); ctx.moveTo(l.x - 180, l.y - 70); ctx.quadraticCurveTo(l.x, l.y - 96, l.x + 180, l.y - 64); ctx.stroke()
            ctx.strokeStyle = withAlpha(pal.snowDeep, 0.9); ctx.lineWidth = 2
            ctx.beginPath(); ctx.moveTo(l.x - 180, l.y - 64); ctx.quadraticCurveTo(l.x, l.y - 90, l.x + 180, l.y - 58); ctx.stroke()
            ctx.restore()
        }],
        footprints: [{ x: l.x - 56, y: baseY - 14, w: 112, h: 16 }, { circle: true, x: l.x - 90, y: baseY + 2, r: 6 }, { circle: true, x: l.x + 90, y: baseY + 2, r: 6 }, { x: l.x - 200, y: l.y - 110, w: 400, h: 30 }],
        lights: [{ x: l.x - 90, y: baseY - 52, r: 70 }, { x: l.x + 90, y: baseY - 52, r: 70 }],
        anchors: { auroraPoint: { x: l.x, y: l.y } }
    }
}

// ---- the islands: the pigeon loft (Share) --------------------------------------------------
// Harbour Isle's fishing village: a tall painted loft on stilts with a
// landing board and a row of holes, two turf-roofed cottages, and a rack of
// fish drying in the wind. The pigeons circle in the motion layer.

function pigeonloft(l) {
    const baseY = l.y - 16
    const cottage = (x, y, flip, seed) => ({
        x, y, box: [-60, -110, 70, 8],
        paint(ctx, pal) {
            const ink = pal.ink
            ctx.save(); if (flip) ctx.scale(-1, 1)
            inkPoly(ctx, [[40, 0], [58, -9], [58, -50], [40, -42]], pal.faluDark, ink)
            inkRect(ctx, -40, -42, 80, 42, pal.falu, ink, { line: 1.7 })
            for (let px = -34; px < 40; px += 7) line(ctx, [[px, -42], [px, 0]], mix(pal.falu, "#000000", 0.2), 1, 0.7)
            turfRoof(ctx, pal, [[-46, -84], [44, -84], [62, -38], [-52, -38]], { seed })
            ctx.strokeStyle = pal.cloth; ctx.lineWidth = 2; ctx.strokeRect(-40, -42, 80, 42)
            inkRect(ctx, -8, -30, 16, 30, pal.door, ink, { line: 1.2 })
            ;[-26, 24].forEach((wx) => { inkRect(ctx, wx - 7, -32, 14, 12, pal.night ? pal.windowLit : pal.window, ink, { line: 1 }); ctx.strokeStyle = pal.cloth; ctx.lineWidth = 1.6; ctx.strokeRect(wx - 7, -32, 14, 12) })
            ctx.restore()
        }
    })
    return {
        sprites: [{
            // the loft on its stilts
            x: l.x, y: baseY, box: [-60, -210, 60, 8],
            paint(ctx, pal) {
                const ink = pal.ink, blue = pal.night ? "#3E5570" : "#6C8FB8"
                ;[-30, 30, -16, 16].forEach((x, i) => line(ctx, [[x, 0], [x * 0.9, -70]], pal.woodDark, i < 2 ? 4 : 2.6))
                line(ctx, [[-30, -30], [30, -50]], pal.woodDark, 2); line(ctx, [[30, -30], [-30, -50]], pal.woodDark, 2)
                // a ladder
                line(ctx, [[40, 0], [30, -72]], pal.wood, 2); line(ctx, [[50, 0], [40, -72]], pal.wood, 2)
                for (let k = 1; k < 7; k++) line(ctx, [[40 - k * 1.4, -k * 10], [50 - k * 1.4, -k * 10]], pal.wood, 1.6)
                inkRect(ctx, -38, -130, 76, 60, pal.cloth, ink, { line: 1.7 })
                for (let x = -32; x < 38; x += 8) line(ctx, [[x, -130], [x, -70]], withAlpha(pal.clothShade, 0.9), 1)
                inkPoly(ctx, [[-46, -128], [0, -168], [46, -128]], blue, ink, { line: 1.7 })
                // the landing board and the holes
                inkRect(ctx, -44, -94, 88, 5, pal.woodLight, ink, { line: 1.2 })
                ;[-24, -8, 8, 24].forEach((x) => { ctx.fillStyle = "#2A2420"; ctx.beginPath(); ctx.arc(x, -104, 5, Math.PI, 0); ctx.lineTo(x + 5, -98); ctx.lineTo(x - 5, -98); ctx.closePath(); ctx.fill() })
                inkEllipse(ctx, 0, -140, 7, 7, pal.night ? pal.windowLit : blue, ink, 1.2)
                // pigeons resting on the ridge and the board
                ;[[-22, -150], [14, -156], [30, -142], [-32, -98], [22, -98]].forEach(([x, y]) => {
                    inkEllipse(ctx, x, y, 5, 3.6, pal.night ? "#8C90A0" : "#B8BCC8", ink, 0.9)
                    inkEllipse(ctx, x + 4, y - 3, 2.6, 2.4, pal.night ? "#7A7E8E" : "#9EA3B2", ink, 0.8)
                    ctx.fillStyle = "#E8894A"; ctx.beginPath(); ctx.moveTo(x + 6.4, y - 3); ctx.lineTo(x + 8.6, y - 2.4); ctx.lineTo(x + 6.4, y - 1.8); ctx.fill()
                })
                sign(ctx, pal, 0, -112, "LOFT", "#FFF6D8", 9)
            }
        }, cottage(l.x - 150, l.y + 40, false, 931), cottage(l.x + 160, l.y + 20, true, 932), {
            // the fish-drying rack
            x: l.x + 30, y: l.y + 110, box: [-70, -70, 70, 6],
            paint(ctx, pal) {
                const ink = pal.ink
                ;[-60, 0, 60].forEach((x) => { line(ctx, [[x - 10, 0], [x, -56]], pal.woodDark, 3); line(ctx, [[x + 10, 0], [x, -56]], pal.woodDark, 3) })
                line(ctx, [[-64, -52], [64, -52]], pal.woodDark, 3)
                for (let i = 0; i < 11; i++) {
                    const x = -54 + i * 11
                    ctx.beginPath(); ctx.moveTo(x, -50); ctx.quadraticCurveTo(x - 4, -36, x, -24); ctx.quadraticCurveTo(x + 4, -36, x, -50)
                    ctx.fillStyle = pal.night ? "#7E7A6E" : "#C9BFA6"; ctx.fill(); ctx.strokeStyle = ink; ctx.lineWidth = 0.8; ctx.stroke()
                }
            }
        }],
        decals: [(ctx, pal) => {
            flagstones(ctx, pal, l.x, l.y + 26, 110, 34, 933)
        }],
        footprints: [{ x: l.x - 36, y: baseY - 10, w: 90, h: 12 }, { x: l.x - 196, y: l.y + 2, w: 116, h: 40 }, { x: l.x + 94, y: l.y - 18, w: 116, h: 40 }, { x: l.x - 40, y: l.y + 104, w: 140, h: 8 }],
        lights: [{ x: l.x, y: baseY - 140, r: 60 }, { x: l.x - 176, y: l.y + 12, r: 55 }, { x: l.x + 136, y: l.y - 8, r: 55 }],
        anchors: { loft: { x: l.x, y: baseY - 150 } }
    }
}

// ---- the islands: the shipwreck (coming soon: Salvage) -------------------------------------
// An old hull on its side on Far Isle's beach, ribs showing, the mast
// broken, and crates and barrels washed up around it.

function shipwreck(l) {
    const baseY = l.y + 10
    return {
        sprites: [{
            x: l.x + 20, y: baseY, box: [-140, -120, 140, 12],
            paint(ctx, pal) {
                const ink = pal.ink, hull = pal.night ? "#3E3226" : "#7A5A3E", hullDark = pal.night ? "#2C241C" : "#5A4230"
                // the hull, heeled over, its planks broken open amidships
                ctx.beginPath(); ctx.moveTo(-130, -6); ctx.quadraticCurveTo(-110, -80, -10, -96); ctx.quadraticCurveTo(90, -100, 128, -40); ctx.quadraticCurveTo(110, 4, 40, 8); ctx.lineTo(-90, 8); ctx.closePath()
                ctx.fillStyle = hull; ctx.fill(); ctx.strokeStyle = ink; ctx.lineWidth = 1.9; ctx.stroke()
                ctx.save(); ctx.clip()
                for (let k = 1; k < 7; k++) { ctx.strokeStyle = withAlpha(hullDark, 0.9); ctx.lineWidth = 1.2; ctx.beginPath(); ctx.moveTo(-130, -6 - k * 12); ctx.quadraticCurveTo(0, -18 - k * 14, 130, -40 - k * 6); ctx.stroke() }
                // the gap, and the ribs across it
                ctx.fillStyle = pal.night ? "#141210" : "#2A2016"
                ctx.beginPath(); ctx.moveTo(-40, -30); ctx.quadraticCurveTo(-10, -86, 50, -74); ctx.quadraticCurveTo(40, -34, -40, -30); ctx.fill()
                for (let x = -30; x < 50; x += 12) line(ctx, [[x, -34], [x + 6, -80]], pal.woodLight, 3)
                ctx.restore()
                line(ctx, [[-130, -6], [128, -40]], withAlpha(pal.sand, 0.9), 3)
                // the broken mast and its tangle of rope
                line(ctx, [[20, -94], [-40, -150]], ink, 6); line(ctx, [[20, -94], [-40, -150]], pal.woodDark, 4)
                ctx.strokeStyle = withAlpha(pal.cloth, 0.8); ctx.lineWidth = 1.2
                ctx.beginPath(); ctx.moveTo(-40, -150); ctx.quadraticCurveTo(-70, -80, -110, -20); ctx.stroke()
                // barnacles and weed
                const r = rng(941)
                for (let i = 0; i < 26; i++) { ctx.fillStyle = r() < 0.5 ? withAlpha(pal.stoneLight, 0.8) : withAlpha(pal.night ? "#2C3A2A" : "#6B7A45", 0.8); ctx.beginPath(); ctx.arc(-110 + r() * 220, -4 - r() * 22, 1.6 + r() * 1.4, 0, Math.PI * 2); ctx.fill() }
            }
        }, crate(l.x - 120, l.y + 60, 18, "MAIL", 0.2), crate(l.x - 84, l.y + 76, 14, "TODO", -0.15), crate(l.x + 110, l.y + 60, 16, "RE:", 0.1), barrel(l.x - 150, l.y + 30, 0.9), barrel(l.x + 150, l.y + 30, 0.8)],
        decals: [(ctx, pal) => {
            // a coil of rope, and a keel's furrow in the sand
            ctx.strokeStyle = withAlpha(pal.cloth, 0.8); ctx.lineWidth = 2
            ;[10, 7, 4].forEach((rad) => { ctx.beginPath(); ctx.ellipse(l.x + 40, l.y + 74, rad * 1.6, rad * 0.8, 0, 0, Math.PI * 2); ctx.stroke() })
            line(ctx, [[l.x - 160, l.y + 24], [l.x - 250, l.y + 60]], withAlpha(pal.sandDark, 0.9), 5)
        }],
        footprints: [{ x: l.x - 110, y: baseY - 30, w: 250, h: 38 }, { circle: true, x: l.x - 120, y: l.y + 56, r: 16 }, { circle: true, x: l.x - 84, y: l.y + 72, r: 12 }, { circle: true, x: l.x + 110, y: l.y + 56, r: 14 }, { circle: true, x: l.x - 150, y: l.y + 26, r: 10 }, { circle: true, x: l.x + 150, y: l.y + 26, r: 9 }],
        lights: [],
        anchors: { wreck: { x: l.x, y: l.y } }
    }
}
